import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from "react-to-print";
import { useParams } from "react-router";
import { useContent } from '../../Content/cms';
import Apirest from '../../Content/Apirest';

import { Participant } from '../../Dashboard/interfaces';
import { ChartData, Score, Chart } from './renderReportCharts';
import { ProtocolResData } from '../Protocol/types';
import { StoreState } from '../../store';
import { FeatureDisplayPopulation, ADD_POPULATION_STATS_DATA } from '../../store/data/types';

import RenderReportCharts from './renderReportCharts';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner';

import './PrintReportStyles.css';

// Type for protocol feature display data from the API

interface ScoreDisplay {
    score: Score;
    chart: Chart;
    population: number;
}

interface FeatureDisplay {
    protocol_item: number;
    lambda_function: number;
    score_displays: ScoreDisplay[];
}

interface FeatureDisplays {
    feature_displays: FeatureDisplay[];
}

// Type for the component props

interface PrintableReportProps {
    show: boolean
    onHide?: () => void
    protocolRes: ProtocolResData
}

const PrintableReport: React.FC<PrintableReportProps> = (props) => {

    let responses = props.protocolRes.responses
    let { participant_id } = useParams<{ participant_id: string }>();

    const contentRef = useRef<HTMLDivElement>(null);

    let getContent = useContent();
    const dispatch = useDispatch();

    let [chartData, setChartData] = React.useState<ChartData[]>([]);

    let participant = useSelector<StoreState, Participant>(
        state => state.participants.find(p => p.id.toString() === participant_id)!
    );

    let populationData = useSelector<StoreState, { [key: string]: FeatureDisplayPopulation }>(
        state => state.data.population_stats_data
    );

    // Load the feaure display and population from apirest
    useEffect(
        () => {
            Apirest.get_protocol_feature_display_pdf(
                props.protocolRes.protocol.id,
                (response: FeatureDisplays) => {
                    const newChartData = flattenFeatureDisplay(response);
                    setChartData(newChartData);
                    loadPopulationData(newChartData);
                }
            );
        }, [props.protocolRes.protocol.id] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Set the chart data population from store when featureDisplayPopulation changes
    useEffect(setPopulationData, [populationData, chartData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(setResponsesValues, [props.protocolRes.responses, chartData]); // eslint-disable-line react-hooks/exhaustive-deps

    function getValueFromResponse(protocol_item_id: number, lambda_function_id: number, scoreName: string): number | undefined | null {
        let protocol_item = responses.find(r => r.protocol_item.id === protocol_item_id);

        if (protocol_item === undefined) {
            return null;
        }

        let feature_extraction_res = protocol_item.feature_extraction_res.find(
            fer => fer.lambda_function.id === lambda_function_id
        );

        if (feature_extraction_res === undefined) {
            return null;
        }

        if (feature_extraction_res.status === 'failed') {
            return null; // The value is not available due to a failure
        }

        if (feature_extraction_res.status === 'pending') {
            return undefined; // The value is not ready yet
        }

        if (feature_extraction_res.scores[scoreName] === undefined) {
            return null; // The score is not available for un unknown reason
        }

        return feature_extraction_res.scores[scoreName];
    }

    function flattenFeatureDisplay(featureDisplay: FeatureDisplays): ChartData[] {
        return featureDisplay.feature_displays.flatMap(fd =>
            fd.score_displays.map(sd => ({
                protocol_item: fd.protocol_item,
                lambda_function: fd.lambda_function,
                score: { name: sd.score.name },
                chart: {
                    name: sd.chart.name,
                    display_configuration: sd.chart.display_configuration
                },
                population: sd.population,
                value: getValueFromResponse(fd.protocol_item, fd.lambda_function, sd.score.name)
            }))
        );
    }

    function setPopulationData() {

        // Filter charData with no population distribution
        let chartDataWithoutPopulation = chartData.filter(
            cd => cd.population_distribution === undefined
        );

        // Generate an array of keys for population distributions
        let existentKeys = chartDataWithoutPopulation.map(cd => {
            return `${cd.chart.name}-${cd.protocol_item}-${cd.lambda_function}-${cd.population}`;
        });

        // get unique existent keys 
        existentKeys = Array.from(new Set(existentKeys)).filter(
            key => key in populationData
        );

        existentKeys.forEach(key => {

            const [chartName, protocolItem, lambdaFunction, population] = key.split('-');

            setChartData(
                prevChartData => {
                    return prevChartData.map(
                        sd => {
                            let correctChartData = (
                                sd.chart.name === chartName &&
                                sd.protocol_item.toString() === protocolItem &&
                                sd.lambda_function.toString() === lambdaFunction &&
                                sd.population.toString() === population
                            );

                            const matchingScore = populationData[key].score_distributions.find(
                                distribution => distribution.score === sd.score.name
                            );

                            if (correctChartData && !matchingScore) {
                                return {
                                    ...sd,
                                    population_distribution: null
                                };
                            }

                            if (correctChartData && matchingScore) {
                                return {
                                    ...sd,
                                    population_distribution: {
                                        fill_color: matchingScore.population.fill_color,
                                        stroke_color: matchingScore.population.stroke_color,
                                        x: matchingScore.distribution.x,
                                        y: matchingScore.distribution.y
                                    }
                                };
                            }
                            return sd;
                        }
                    );
                }
            )
        });
    }

    function setResponsesValues() {

        // Filter charData with no value distribution
        let newValues: [number, number | null][] = chartData.map(
            (cd, idx) => {

                if (cd.value !== undefined) {
                    return [-1, null] as [number, number | null]; // Skip if value is already set
                }

                let value = getValueFromResponse(
                    cd.protocol_item,
                    cd.lambda_function,
                    cd.score.name
                );

                if (value === undefined) {
                    return [-1, null] as [number, number | null]; // Skip if value is not ready yet
                }

                return [idx, value] as [number, number | null];
            }
        ).filter(v => v[0] !== -1);

        if (newValues.length === 0) {
            return;
        }

        setChartData(prevChartData => {
            let newChartData = [...prevChartData];

            // Update the chartData with the new values 
            newValues.forEach(([idx, value]) => {
                newChartData[idx] = {
                    ...newChartData[idx],
                    value: value
                };
            });
            return newChartData;
        });
    }

    function loadPopulationData(newChartData: ChartData[]) {

        // Generate an array of keys for population distributions
        let uniqueKeys = newChartData.map(cd => {
            return `${cd.chart.name}-${cd.protocol_item}-${cd.lambda_function}-${cd.population}`;
        });

        // Remove duplicates by converting the array to a Set and back to an array
        uniqueKeys = Array.from(new Set(uniqueKeys)).filter(
            key => !(key in populationData)
        );

        uniqueKeys.forEach(key => {
            const [chartName, protocolItem, lambdaFunction, population] = key.split('-');

            Apirest.get_feature_display_population_stats(
                chartName,
                protocolItem,
                lambdaFunction,
                population,
                participant.locale.lang.id.toString(),
                (populationData: FeatureDisplayPopulation) => {
                    dispatch({
                        type: ADD_POPULATION_STATS_DATA,
                        payload: { [key]: populationData }
                    });
                }
            );
        });
    }

    function mapProtocolItemTitles(responses: any[]): Record<number, string> {
        const result: Record<number, string> = {};
        responses.forEach(response => {
            const item = response.protocol_item;
            result[item.id] = item.title;
        });
        return result;
    }

    function mapLambdaFunctions(responses: any[]): Record<number, string> {
        const map: Record<number, string> = {};
        responses.forEach(response => {
            response.feature_extraction_res.forEach((res: any) => {
                const func = res.lambda_function;
                map[func.id] = func.name;
            });
        });
        return map;
    }

    const handlePrint = useReactToPrint({
        content: () => contentRef.current,
        copyStyles: true
    });

    let itemTitles = mapProtocolItemTitles(responses);
    let ferFuncNames = mapLambdaFunctions(responses);
    let protocolName = props.protocolRes.protocol.name;

    return (
        <Modal
            show={props.show}
            onHide={props.onHide}
            scrollable
            size='xl'
        >
            <Modal.Header className={'mb-0 pb-0 mr-2'} closeButton>
                <Modal.Title className={' pl-2 pt-0 w-100'}>
                    <Row>
                        {getContent('printable_report__modal_title')}
                    </Row>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body ref={contentRef} className={'no-border'}>

                {/*CONTAINER WITH INFORMATION ABOUT REPORT AND PATIENT*/}
                <Container className={'mb-3'}>
                    <Row className={'mb-2'}>
                        <Col>
                            <p className={''}>{getContent('printable_report__modal_text')}</p>
                        </Col>
                    </Row>
                    <Row className={'mb-2'}>
                        <Col>
                            <h6>{getContent('printable_report__modal_participant_number__title') + " " + (participant.code || '')}</h6>
                            <h6>{getContent('printable_report__modal_participant_name__title') + " " + ((participant.first_name || '') + ' ' + (participant.last_name || ''))}</h6>
                        </Col>
                    </Row>
                </Container>

                {/*CONTAINER FOR CHARTS*/}
                <Container className='mt-3'>
                    <Row>
                        <Col>
                            <h4 className={'fw-bolder text-primary pb-3'}>
                                {protocolName}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {chartData && <RenderReportCharts
                                data={chartData}
                                itemTitles={itemTitles}
                                ferFuncNames={ferFuncNames}
                            />}
                            {!chartData && <Spinner
                                animation={"border"}
                                role={"status"}
                                className={'mt-5'}
                            />}
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer className="mt-auto">
                <Button variant="primary" onClick={handlePrint}>
                    {getContent('printable_report__modal__print_button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default PrintableReport;
